/**
 * AdminFormulariosFiscalizacion.jsx · v2.8.9 · Fase 2 Drag & Drop Builder
 * ────────────────────────────────────────────────────────────────────────
 * Constructor visual de plantillas de fiscalización con:
 *  · Paleta lateral (izquierda) con tipos de campo arrastrables
 *  · Canvas central sortable (drag-reorder entre secciones)
 *  · Preview mobile en vivo (derecha) mostrando cómo se ve en la app técnico
 *  · Vinculación al Tipo de Solución seleccionado (persistencia en
 *    tabla `fiscal_form_definitions` vía PUT /v1/fiscalizacion/definitions/{id})
 */
import { useState, useEffect, useMemo } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import {
  ClipboardList, Save, Sparkles, Package, Camera, Zap, PenLine,
  CheckSquare, Type, Hash, GripVertical, Trash2, Plus, Smartphone,
  Signal, ChevronRight, AlertCircle,
} from "lucide-react";

// Paleta de tipos de campo — arrastrables desde la sidebar
const FIELD_TYPES = [
  { value: "photo",         label: "Foto",           icon: Camera,     color: "#0EA5E9", hint: "Captura de cámara con validación" },
  { value: "text",          label: "Texto",          icon: Type,       color: "#334155", hint: "Texto libre de una línea" },
  { value: "textarea",      label: "Texto largo",    icon: Type,       color: "#475569", hint: "Área multilínea (observaciones)" },
  { value: "number",        label: "Número",         icon: Hash,       color: "#7C3AED", hint: "Entrada numérica" },
  { value: "checkbox",      label: "Sí / No",        icon: CheckSquare,color: "#059669", hint: "Booleano" },
  { value: "power_reading", label: "Potencia dBm",   icon: Zap,        color: "#F59E0B", hint: "Lectura óptica con rango" },
  { value: "signature",     label: "Firma",          icon: PenLine,    color: "#DC2626", hint: "Firma digital cliente" },
  { value: "materials",     label: "Materiales",     icon: Package,    color: "#EA580C", hint: "Bloque desde receta" },
];

const DRAG_MIME_NEW = "application/x-alfa-fieldtype";
const DRAG_MIME_MOVE = "application/x-alfa-fieldmove";

export default function AdminFormulariosFiscalizacion() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [definition, setDefinition] = useState([]);
  const [stInfo, setStInfo] = useState(null);
  const [isDefault, setIsDefault] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [dragOver, setDragOver] = useState(null); // {si, fi} para highlight

  const fetchList = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v1/fiscalizacion/definitions");
      setList(data.items || []);
    } catch { toast.error("Error cargando lista"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchList(); }, []);

  const selectST = async (id) => {
    if (dirty && !window.confirm("Hay cambios sin guardar en el formulario actual. ¿Descartar?")) return;
    setSelectedId(id);
    try {
      const { data } = await api.get(`/v1/fiscalizacion/definitions/${id}`);
      setDefinition(data.definition || []);
      setStInfo(data.solution_type);
      setIsDefault(data.is_default);
      setDirty(false);
    } catch { toast.error("Error cargando plantilla"); }
  };

  const applyPreset = async (preset) => {
    if (!selectedId) return;
    if (!window.confirm(`¿Aplicar preset '${preset}'? Sobrescribe la plantilla actual.`)) return;
    try {
      await api.post(`/v1/fiscalizacion/definitions/${selectedId}/apply-preset?preset=${preset}`);
      toast.success("Preset aplicado");
      await selectST(selectedId);
      await fetchList();
    } catch { toast.error("Error aplicando preset"); }
  };

  const saveDef = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await api.put(`/v1/fiscalizacion/definitions/${selectedId}`, { definition });
      toast.success("Plantilla guardada en fiscal_form_definitions");
      await fetchList();
      setIsDefault(false);
      setDirty(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error guardando"); }
    finally { setSaving(false); }
  };

  // ═══ Ops sobre la definición (todas marcan dirty) ═══
  const mark = (next) => { setDefinition(next); setDirty(true); };
  const addSection = () => mark([...definition, { title: "Nueva Sección", fields: [] }]);
  const removeSection = (si) => mark(definition.filter((_, i) => i !== si));
  const updateSection = (si, patch) => mark(definition.map((s, i) => i === si ? { ...s, ...patch } : s));
  const updateField = (si, fi, patch) => {
    const fields = [...definition[si].fields];
    fields[fi] = { ...fields[fi], ...patch };
    updateSection(si, { fields });
  };
  const removeField = (si, fi) => {
    const fields = definition[si].fields.filter((_, i) => i !== fi);
    updateSection(si, { fields });
  };

  // ═══ Drag & Drop handlers ═══
  const onDragStartPalette = (e, ftValue) => {
    e.dataTransfer.setData(DRAG_MIME_NEW, ftValue);
    e.dataTransfer.effectAllowed = "copy";
  };
  const onDragStartField = (e, si, fi) => {
    e.dataTransfer.setData(DRAG_MIME_MOVE, JSON.stringify({ si, fi }));
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOverField = (e, si, fi) => {
    e.preventDefault();
    setDragOver({ si, fi });
  };
  const onDragOverSection = (e, si) => {
    e.preventDefault();
    setDragOver({ si, fi: -1 });
  };
  const onDragLeave = () => setDragOver(null);

  const dropField = (e, targetSi, targetFi) => {
    e.preventDefault();
    setDragOver(null);
    const newType = e.dataTransfer.getData(DRAG_MIME_NEW);
    const moveData = e.dataTransfer.getData(DRAG_MIME_MOVE);

    if (newType) {
      // Insert nuevo campo desde paleta
      const ftDef = FIELD_TYPES.find((t) => t.value === newType);
      const newField = {
        key: `field_${newType}_${Date.now().toString(36).slice(-4)}`,
        label: ftDef?.label || "Campo",
        type: newType,
        required: false,
      };
      const next = definition.map((sec, i) => {
        if (i !== targetSi) return sec;
        const fields = [...(sec.fields || [])];
        const insertAt = targetFi === -1 ? fields.length : targetFi;
        fields.splice(insertAt, 0, newField);
        return { ...sec, fields };
      });
      mark(next);
      return;
    }

    if (moveData) {
      const { si: fromSi, fi: fromFi } = JSON.parse(moveData);
      if (fromSi === targetSi && fromFi === targetFi) return;
      const next = definition.map((s) => ({ ...s, fields: [...(s.fields || [])] }));
      const [moved] = next[fromSi].fields.splice(fromFi, 1);
      let insertAt = targetFi === -1 ? next[targetSi].fields.length : targetFi;
      // Compensación cuando se mueve hacia adelante en la misma sección
      if (fromSi === targetSi && fromFi < insertAt) insertAt -= 1;
      next[targetSi].fields.splice(insertAt, 0, moved);
      mark(next);
    }
  };

  const totalFields = useMemo(
    () => definition.reduce((a, s) => a + (s.fields?.length || 0), 0),
    [definition]
  );

  return (
    <Layout title="Constructor de Formularios · Fiscalización" subtitle="Drag & Drop · Fase 2">
      <div className="max-w-[1600px] mx-auto" data-testid="fiscal-builder">
        <div className="flex gap-4 h-[calc(100vh-190px)] min-h-[600px]">
          {/* ═══ Sidebar Solution Types ═══ */}
          <aside className="w-56 flex-shrink-0 bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col">
            <div className="px-3 py-2 border-b border-gray-200 bg-[#0A3747] text-white text-[10px] font-mono uppercase tracking-widest">
              Tipos de Solución
            </div>
            <div className="flex-1 overflow-y-auto p-2" data-testid="list-solution-types">
              {loading && <div className="text-xs text-gray-400 py-2">Cargando...</div>}
              {!loading && list.length === 0 && <div className="text-xs text-gray-400">Sin tipos de solución.</div>}
              {list.map((st) => (
                <button
                  key={st.solution_type_id}
                  onClick={() => selectST(st.solution_type_id)}
                  className={`w-full text-left p-2 rounded mb-1 text-xs ${selectedId === st.solution_type_id ? "bg-[#E0E621]/50 border-2 border-[#0A3747]" : "hover:bg-gray-50 border border-gray-100"}`}
                  data-testid={`st-${st.solution_type_id}`}
                >
                  <div className="font-mono text-[9px] text-[#0A3747] font-bold">{st.code}</div>
                  <div className="font-semibold text-gray-900 truncate">{st.name}</div>
                  <div className="text-[9px] text-gray-500 mt-0.5">
                    {st.has_custom ? <span className="text-emerald-700">● Custom</span> : <span className="text-gray-400">○ Default</span>}
                    {st.fields_count > 0 && <span className="ml-1">· {st.fields_count} campos</span>}
                  </div>
                </button>
              ))}
            </div>
          </aside>

          {/* ═══ Paleta de campos (arrastrable) ═══ */}
          <aside className="w-48 flex-shrink-0 bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col" data-testid="field-palette">
            <div className="px-3 py-2 border-b border-gray-200 bg-[#0A3747] text-white text-[10px] font-mono uppercase tracking-widest">
              Paleta · Arrastra
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
              {FIELD_TYPES.map((ft) => {
                const Icon = ft.icon;
                return (
                  <div
                    key={ft.value}
                    draggable
                    onDragStart={(e) => onDragStartPalette(e, ft.value)}
                    className="p-2 rounded border border-gray-200 bg-white hover:border-[#0A3747] hover:shadow cursor-grab active:cursor-grabbing transition"
                    data-testid={`palette-${ft.value}`}
                    title={ft.hint}
                  >
                    <div className="flex items-center gap-2">
                      <Icon size={14} style={{ color: ft.color }} />
                      <span className="text-xs font-semibold">{ft.label}</span>
                    </div>
                    <div className="text-[9px] text-gray-500 mt-0.5 leading-tight">{ft.hint}</div>
                  </div>
                );
              })}
            </div>
          </aside>

          {/* ═══ Canvas central (Builder) ═══ */}
          <main className="flex-1 min-w-0 bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col">
            {!selectedId && (
              <div className="flex-1 flex items-center justify-center text-gray-400 text-sm" data-testid="builder-empty">
                <div className="text-center">
                  <ClipboardList size={40} className="mx-auto mb-2 opacity-40" />
                  Selecciona un Tipo de Solución para comenzar
                </div>
              </div>
            )}
            {selectedId && (
              <>
                {/* Header con acciones */}
                <div className="px-4 py-2.5 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-3 flex-wrap" data-testid="builder-header">
                  <div className="min-w-0">
                    <div className="text-[10px] font-mono text-[#0A3747] font-bold">{stInfo?.code}</div>
                    <div className="text-sm font-semibold text-gray-900 truncate">{stInfo?.name}</div>
                    {isDefault && <div className="text-[10px] text-yellow-700 flex items-center gap-1 mt-0.5"><AlertCircle size={10}/> Sin plantilla propia (usando default)</div>}
                    {dirty && <div className="text-[10px] text-orange-600 flex items-center gap-1 mt-0.5">● Cambios sin guardar</div>}
                  </div>
                  <div className="flex gap-2 items-center">
                    <button onClick={() => applyPreset("ftth_install")} className="px-2 py-1 text-[11px] rounded bg-[#0EA5E9] text-white flex items-center gap-1" data-testid="preset-ftth">
                      <Sparkles size={11}/> FTTH
                    </button>
                    <button onClick={() => applyPreset("remove_equipment")} className="px-2 py-1 text-[11px] rounded bg-slate-600 text-white flex items-center gap-1" data-testid="preset-retiro">
                      <Sparkles size={11}/> Retiro
                    </button>
                    <button
                      onClick={saveDef}
                      disabled={saving || !dirty}
                      className="px-3 py-1.5 text-xs rounded bg-emerald-600 text-white font-bold disabled:opacity-40 flex items-center gap-1"
                      data-testid="save-def"
                    >
                      <Save size={12}/> {saving ? "Guardando..." : "Guardar"}
                    </button>
                  </div>
                </div>

                {/* Canvas */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="builder-canvas">
                  {definition.map((section, si) => (
                    <div key={si} className="border-2 border-gray-200 rounded-lg p-3 bg-gray-50" data-testid={`section-${si}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <input
                          value={section.title}
                          onChange={(e) => updateSection(si, { title: e.target.value })}
                          className="flex-1 px-2 py-1 rounded border text-sm font-bold bg-white"
                          placeholder="Título de la sección"
                          data-testid={`section-title-${si}`}
                        />
                        <button
                          onClick={() => removeSection(si)}
                          className="text-red-600 hover:bg-red-50 text-xs px-2 py-1 rounded flex items-center gap-1"
                          data-testid={`remove-section-${si}`}
                        >
                          <Trash2 size={12}/> Quitar sección
                        </button>
                      </div>

                      <div className="space-y-1.5 min-h-[40px]"
                           onDragOver={(e) => onDragOverSection(e, si)}
                           onDragLeave={onDragLeave}
                           onDrop={(e) => dropField(e, si, -1)}
                           data-testid={`section-dropzone-${si}`}>
                        {(section.fields || []).length === 0 && (
                          <div className={`text-center text-xs py-4 border-2 border-dashed rounded ${dragOver?.si === si ? "border-[#0A3747] bg-blue-50 text-[#0A3747]" : "border-gray-300 text-gray-400"}`}>
                            Arrastra un campo aquí desde la paleta →
                          </div>
                        )}
                        {(section.fields || []).map((f, fi) => {
                          const ftDef = FIELD_TYPES.find((t) => t.value === f.type) || FIELD_TYPES[0];
                          const Icon = ftDef.icon;
                          const isHovered = dragOver?.si === si && dragOver?.fi === fi;
                          return (
                            <div
                              key={fi}
                              draggable
                              onDragStart={(e) => onDragStartField(e, si, fi)}
                              onDragOver={(e) => onDragOverField(e, si, fi)}
                              onDragLeave={onDragLeave}
                              onDrop={(e) => dropField(e, si, fi)}
                              className={`flex items-center gap-2 bg-white rounded border-2 p-1.5 cursor-move transition ${isHovered ? "border-[#0A3747] bg-blue-50" : "border-gray-200 hover:border-gray-400"}`}
                              data-testid={`field-${si}-${fi}`}
                            >
                              <GripVertical size={12} className="text-gray-400" />
                              <Icon size={14} style={{ color: ftDef.color }} />
                              <input
                                value={f.key}
                                onChange={(e) => updateField(si, fi, { key: e.target.value.replace(/[^a-z0-9_]/g, "_") })}
                                className="w-28 px-1.5 py-0.5 text-[10px] font-mono border rounded"
                                placeholder="key"
                                data-testid={`field-key-${si}-${fi}`}
                              />
                              <input
                                value={f.label}
                                onChange={(e) => updateField(si, fi, { label: e.target.value })}
                                className="flex-1 min-w-0 px-1.5 py-0.5 text-xs border rounded"
                                placeholder="Etiqueta visible al técnico"
                                data-testid={`field-label-${si}-${fi}`}
                              />
                              <select
                                value={f.type}
                                onChange={(e) => updateField(si, fi, { type: e.target.value })}
                                className="text-[10px] border rounded px-1 py-0.5 flex-shrink-0"
                                data-testid={`field-type-${si}-${fi}`}
                              >
                                {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                              </select>
                              <label className="flex items-center gap-1 text-[10px] flex-shrink-0" title="Obligatorio">
                                <input type="checkbox" checked={!!f.required} onChange={(e) => updateField(si, fi, { required: e.target.checked })} data-testid={`field-req-${si}-${fi}`}/>
                                Obl.
                              </label>
                              <button onClick={() => removeField(si, fi)} className="text-red-600 hover:bg-red-50 text-xs p-1 rounded flex-shrink-0" data-testid={`remove-field-${si}-${fi}`}>
                                <Trash2 size={12}/>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={addSection}
                    className="w-full py-2 rounded border-2 border-dashed border-gray-300 text-sm text-gray-500 hover:bg-gray-50 flex items-center justify-center gap-1"
                    data-testid="add-section"
                  >
                    <Plus size={14}/> Añadir sección
                  </button>
                  <div className="text-[10px] text-gray-400 text-center pt-2 font-mono">
                    Total: {definition.length} sección(es) · {totalFields} campo(s)
                  </div>
                </div>
              </>
            )}
          </main>

          {/* ═══ Preview mobile en vivo ═══ */}
          <aside className="w-80 flex-shrink-0 flex flex-col" data-testid="mobile-preview">
            <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-2 flex items-center gap-1">
              <Smartphone size={11}/> Preview técnico móvil
            </div>
            <div className="flex-1 bg-gray-800 rounded-3xl p-3 shadow-xl overflow-hidden">
              <div className="bg-white rounded-2xl h-full flex flex-col overflow-hidden">
                {/* Status bar mock */}
                <div className="px-4 py-1 bg-white flex items-center justify-between text-[9px] text-gray-600 border-b">
                  <span className="font-semibold">9:41</span>
                  <div className="flex items-center gap-1"><Signal size={9}/><span>100%</span></div>
                </div>
                {/* Header app */}
                <div className="px-3 py-2 bg-[#0A3747] text-white">
                  <div className="text-[8px] font-mono uppercase tracking-widest text-[#E0E621]">Fiscalización</div>
                  <div className="text-xs font-bold truncate">{stInfo?.name || "Selecciona un tipo"}</div>
                </div>
                {/* Cuerpo */}
                <div className="flex-1 overflow-y-auto p-2 space-y-2 text-xs">
                  {(!selectedId || definition.length === 0) && (
                    <div className="text-center text-gray-400 py-6 text-[11px]">Sin campos aún.</div>
                  )}
                  {definition.map((section, si) => (
                    <div key={si} className="bg-gray-50 rounded p-2 border border-gray-200">
                      <div className="text-[10px] font-bold text-[#0A3747] mb-1.5 flex items-center gap-1">
                        <ChevronRight size={10}/> {section.title}
                      </div>
                      <div className="space-y-1.5">
                        {(section.fields || []).map((f, fi) => (
                          <MobileFieldPreview key={fi} field={f} />
                        ))}
                        {(section.fields || []).length === 0 && (
                          <div className="text-[9px] text-gray-400 italic">Sin campos.</div>
                        )}
                      </div>
                    </div>
                  ))}
                  {definition.length > 0 && (
                    <button
                      disabled
                      className="w-full mt-3 py-2 rounded bg-emerald-600 text-white text-xs font-bold flex items-center justify-center gap-1 opacity-90"
                    >
                      <PenLine size={11}/> Firmar y enviar
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="text-[9px] text-gray-400 text-center mt-2">
              Persistencia: <span className="font-mono">fiscal_form_definitions</span>
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Preview individual de un campo en mobile mock
// ═══════════════════════════════════════════════════════════════════════
function MobileFieldPreview({ field }) {
  const ftDef = FIELD_TYPES.find((t) => t.value === field.type) || FIELD_TYPES[0];
  const Icon = ftDef.icon;
  const req = field.required && <span className="text-red-500 ml-0.5">*</span>;
  return (
    <div className="bg-white rounded border border-gray-200 p-1.5">
      <div className="text-[10px] font-semibold text-gray-800 flex items-center gap-1 mb-1">
        <Icon size={9} style={{ color: ftDef.color }} />
        {field.label || "(sin etiqueta)"} {req}
      </div>
      {field.type === "photo" && (
        <div className="h-12 bg-gray-100 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 text-[9px]">
          <Camera size={12} className="mr-1"/> Tocar para tomar foto
        </div>
      )}
      {field.type === "text" && (
        <div className="h-6 bg-gray-100 rounded border border-gray-200 text-[9px] text-gray-400 px-1.5 flex items-center">Escribe...</div>
      )}
      {field.type === "textarea" && (
        <div className="h-10 bg-gray-100 rounded border border-gray-200 text-[9px] text-gray-400 px-1.5 py-1">Observaciones...</div>
      )}
      {field.type === "number" && (
        <div className="h-6 bg-gray-100 rounded border border-gray-200 text-[9px] text-gray-400 px-1.5 flex items-center">0</div>
      )}
      {field.type === "checkbox" && (
        <div className="flex items-center gap-1 text-[9px] text-gray-500"><input type="checkbox" disabled className="w-3 h-3"/> Sí</div>
      )}
      {field.type === "power_reading" && (
        <div className="h-6 bg-yellow-50 border border-yellow-200 rounded text-[9px] text-yellow-700 px-1.5 flex items-center gap-1">
          <Zap size={9}/> -25.0 dBm
        </div>
      )}
      {field.type === "signature" && (
        <div className="h-10 bg-gray-100 rounded border-2 border-dashed border-gray-300 flex items-center justify-center text-[9px] text-gray-400">
          Firma aquí
        </div>
      )}
      {field.type === "materials" && (
        <div className="bg-orange-50 border border-orange-200 rounded p-1 text-[9px] text-orange-800">
          <Package size={9} className="inline mr-1"/> Materiales de la receta
        </div>
      )}
    </div>
  );
}
